import {
  Button,
  ButtonColor,
  ButtonSize,
  pluralize,
} from '@leland-dev/leland-ui-library';
import React, {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { type SubCategory } from '../../__generated-gql-types__/globalTypes';
import { initializeApollo } from '../../lib/apolloClient';
import useMediaQuery, { Breakpoints } from '../../utils/hooks/useMediaQuery';
import { type OfferingPackagesFilterInputs } from '../../utils/package';
import { type SchoolFilterHookOutput } from '../filters/SchoolFilters';
import OfferingPackageCard from '../package/OfferingPackageCard';
import OfferingPackageCardSkeleton from '../package/OfferingPackageCardSkeleton';

import FeaturedServicesFilter from './FeaturedServicesFilter';
import OfferingPackagesFilterButton from './OfferingPackagesFilterButton';
import {
  type OfferingPackagesCategoryFragment,
  type OfferingPackagesOfferingPackageFragment,
  OfferingPackagesSearchPackagesDocument,
  type OfferingPackagesSearchPackagesQuery,
  type OfferingPackagesSearchPackagesQueryVariables,
  type OfferingPackagesSubCategoryFragment,
  useOfferingPackagesLelandPackagesQuery,
} from './__generated-gql-types__/OfferingPackages.generated';

export type OfferingPackagesData = OfferingPackagesSearchPackagesQuery;

export const getStaticOfferingPackagesProps = async ({
  category,
  subCategory,
}: {
  category: OfferingPackagesCategoryFragment;
  subCategory: Possible<SubCategory>;
}): Promise<OfferingPackagesSearchPackagesQuery> => {
  const apolloClient = initializeApollo();
  const offeringPackagesQuery = await apolloClient.query<
    OfferingPackagesSearchPackagesQuery,
    OfferingPackagesSearchPackagesQueryVariables
  >({
    query: OfferingPackagesSearchPackagesDocument,
    variables: {
      categoryUrn: category.id,
      ...(subCategory ? { subCategoryUrns: [subCategory.id] } : {}),
      start: 0,
      count: DEFAULT_OFFERING_PACKAGES_PAGE_SIZE,
    },
  });
  return offeringPackagesQuery.data;
};

interface OfferingPackagesProps {
  category: OfferingPackagesCategoryFragment;
  className?: string;
  initialData?: OfferingPackagesSearchPackagesQuery;
  onClear?: () => void;
  onClick?: (p: OfferingPackagesOfferingPackageFragment, index: number) => void;
  onClickViewMore?: () => void;
  schoolsSort?: SchoolFilterHookOutput;
  searchQuery?: Nullable<string>;
  subTitle?: ReactNode;
  subCategory: Possible<OfferingPackagesSubCategoryFragment>;
  title?: string;
  hideFilters?: boolean;
}

const DEFAULT_OFFERING_PACKAGES_PAGE_SIZE = 4;

const OfferingPackages: React.FC<OfferingPackagesProps> = ({
  category,
  className,
  initialData,
  onClear,
  onClick,
  onClickViewMore,
  schoolsSort,
  searchQuery,
  subTitle,
  title,
  hideFilters = false,
}) => {
  const isMd = useMediaQuery(Breakpoints.MD);
  const isTablet = useMediaQuery(Breakpoints.TAB);

  const { data: universalPackagesData, loading: universalPackagesLoading } =
    useOfferingPackagesLelandPackagesQuery({
      variables: {
        categoryUrn: category.id,
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    });

  const [showAllUniversalPackages, setShowAllUniversalPackages] =
    useState(false);

  const universalPackages = useMemo(() => {
    if (universalPackagesData) {
      return universalPackagesData.lelandPackages.results.filter((p) => {
        return p.prices?.length && p.prices.every((price) => !!price.sku);
      });
    }
    return [];
  }, [universalPackagesData]);

  const packagesData: OfferingPackagesOfferingPackageFragment[] =
    showAllUniversalPackages
      ? universalPackages
      : universalPackages.slice(0, DEFAULT_OFFERING_PACKAGES_PAGE_SIZE);

  const packagesTotal = universalPackages.length;

  const totalPackagesWithNoFilters = packagesTotal ?? 0;

  const isLoading = !initialData && universalPackagesLoading;
  const isEmptyState = !isLoading && packagesData.length === 0;

  const [selectedFilters, setSelectedFilters] =
    useState<Nullable<OfferingPackagesFilterInputs>>(null);
  const [isViewingMore, setIsViewingMore] = useState(false);

  const hideServicesFilter = !!(
    !isTablet ||
    isEmptyState ||
    (selectedFilters?.coachingFocusListFilter &&
      selectedFilters.coachingFocusListFilter.length > 0) ||
    hideFilters
  );

  useEffect(() => {
    setSelectedFilters(null);
  }, [category]);

  const onViewMorePackages = useCallback(() => {
    setIsViewingMore(true);
    setShowAllUniversalPackages(true);
    setIsViewingMore(false);

    if (onClickViewMore) {
      onClickViewMore();
    }
  }, [onClickViewMore]);

  const onRefetchPackages = useCallback(
    async (variables: OfferingPackagesFilterInputs) => {
      setSelectedFilters(variables);
      await Promise.resolve(); // Make compiler happy
    },
    [],
  );

  const clearFilters = async () => {
    setSelectedFilters(null);
    await onRefetchPackages({
      keyword: null,
      priceFilter: null,
      coachingFocusListFilter: null,
    });
    onClear?.();
  };

  const sectionTitle =
    !isLoading && searchQuery
      ? `${
          packagesTotal > 0
            ? pluralize(packagesTotal, 'package')
            : 'No packages'
        } matching "${searchQuery}"`
      : title;

  const sectionSubtitle = isEmptyState ? (
    <span>
      There are no results matching your criteria. Adjust or{' '}
      <button
        type="button"
        className="text-leland-red underline underline-offset-2"
        onClick={clearFilters}
        id="offering_packages:clear_filters"
      >
        clear
      </button>{' '}
      your filters to expand your search.
    </span>
  ) : (
    subTitle
  );

  const TITLE_ROW = (
    <div className="flex flex-col items-center justify-between md:flex-row md:space-x-4">
      <div>
        <h2 className="text-2xl font-medium text-leland-gray-dark">
          {sectionTitle}
        </h2>
        <p className="mt-2 text-lg font-normal leading-6 text-leland-gray-light">
          {sectionSubtitle}
        </p>
      </div>
      {!hideFilters && schoolsSort ? (
        <OfferingPackagesFilterButton
          className="mt-4 md:mt-0"
          categoryUrn={category.id}
          coachingFocuses={category.coachingFocusList}
          comprehensiveFocuses={category.comprehensiveFocusList?.map(
            (focus) => focus.id,
          )}
          defaultValues={selectedFilters}
          defaultTotalOfferingPackages={packagesTotal}
          onRefetchPackages={onRefetchPackages}
          clearFilters={clearFilters}
          schoolFilters={schoolsSort}
        />
      ) : null}
    </div>
  );

  return (
    <>
      {isLoading && !isViewingMore ? (
        <div className={className}>
          {TITLE_ROW}
          <div className="mt-6 grid md:mt-8 md:grid-cols-3 md:gap-6 lg:grid-cols-4">
            {Array.from({ length: isMd ? 4 : 3 }).map((_, index) => (
              <OfferingPackageCardSkeleton key={index} />
            ))}
          </div>
        </div>
      ) : totalPackagesWithNoFilters > 0 ? (
        <div className={className}>
          {TITLE_ROW}
          {hideServicesFilter ? null : (
            <FeaturedServicesFilter
              category={category}
              applyFilter={(coachingFocusListFilter) =>
                onRefetchPackages({
                  ...selectedFilters,
                  coachingFocusListFilter,
                })
              }
            />
          )}
          {packagesData.length > 0 ? (
            <>
              <div className="mt-6 grid md:mt-8 md:grid-cols-3 md:gap-6 lg:grid-cols-4">
                {packagesData.map((offeringPackage, idx) => (
                  <OfferingPackageCard
                    key={offeringPackage.id}
                    offeringPackage={offeringPackage}
                    opportunityCoach={null}
                    onClick={
                      onClick ? () => onClick(offeringPackage, idx) : undefined
                    }
                  />
                ))}
              </div>
              {packagesTotal > DEFAULT_OFFERING_PACKAGES_PAGE_SIZE &&
              packagesData.length < packagesTotal ? (
                <div className="mt-6 flex w-full items-center md:mt-10">
                  <Button
                    label="View more packages"
                    onClick={onViewMorePackages}
                    buttonColor={ButtonColor.WHITE}
                    size={ButtonSize.LARGE}
                    disabled={isViewingMore}
                    id="offering_packages:view_more_packages"
                  />
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </>
  );
};

export default OfferingPackages;
